import config from '@/config';
import { USER_ROLE } from '@/constants';
import type { AuthCognito } from '@shared/config';

import { AUTH_KEY, IS_SUBSCRIBED_KEY } from './direct-auth';

/**
 * The reader's account, for the tenants that sign in through Cognito's
 * hosted UI. Shared by the menus' account buttons (`@shared/ui/menu`) and
 * Settings → Account, so the two can't disagree about who is signed in or
 * where Sign in goes.
 */
export interface CognitoAccount {
	isAuthenticated: boolean;
	isSubscribed: boolean;
	/** Signed in as an editor, previewing drafts. */
	isAdmin: boolean;
	/** The tenant's customer portal, when it has one. */
	customerPortalUrl: string | null;
}

/** `null` unless this tenant uses Cognito sign-in. */
export function readCognitoAccount(): CognitoAccount | null {
	const auth = config?.app?.auth;
	if (!auth?.enabled || auth.type !== 'cognito') {
		return null;
	}
	return {
		isAuthenticated: !!localStorage.getItem(AUTH_KEY),
		isSubscribed: !!localStorage.getItem(IS_SUBSCRIBED_KEY),
		isAdmin: localStorage.getItem(USER_ROLE) === 'admin',
		customerPortalUrl:
			(auth as AuthCognito).config?.customerPortalUrl ?? null,
	};
}

/**
 * A Cognito hosted-UI URL. Sign-in and sign-up come back to `/callback`
 * with the current path as `state`; sign-out comes back to `/logout`.
 * `basepath` is the router's (`useRouter().basepath`).
 */
export function cognitoHostedUrl(
	page: 'login' | 'signup' | 'logout',
	basepath: string,
): string {
	const auth = config.app.auth as AuthCognito;
	const base = basepath.replace(/\/$/, '');
	const origin = `${location.protocol}//${location.host}`;
	const params =
		page === 'logout'
			? [
					`client_id=${auth?.config?.clientId}`,
					`logout_uri=${origin}${base}/logout`,
				]
			: [
					`client_id=${auth.config.clientId}`,
					'response_type=code',
					'scope=email+openid',
					`redirect_uri=${origin}${base}/callback`,
					`state=${btoa(window.location.pathname)}`,
				];
	return `${auth?.config?.host}/${page}?${params.join('&')}`;
}
